'use client';

import { type ThreeEvent } from '@react-three/fiber';
import { AnimatePresence, motion } from 'motion/react';
import dynamic from 'next/dynamic';
import { useMemo, useRef, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { z } from 'zod';

import TriangleLeft from '~/assets/icons/triangle--left.svg';
import TriangleRight from '~/assets/icons/triangle--right.svg';
import Heading from '~/components/atoms/Heading';
import GenericFallback from '~/components/molecules/GenericFallback';
import Media from '~/components/molecules/Media';
import PageLink from '~/components/molecules/PageLink';
import QuatrefoilFloater from '~/components/molecules/QuatrefoilFloater';
import TextSplitter from '~/components/molecules/TextSplitter';
import { schemas } from '~/components/organisms/slices/content-blocks/@types/schemas';
import { useCarousel } from '~/lib/hooks/useCarousel';
import { useIdleLoad } from '~/lib/hooks/useIdleLoading';
import { MediaItem } from '~/lib/hooks/useMixedTextures';
import { useHydrationSafeResponsive } from '~/lib/hooks/useResponsive';

const AaltoCanvas = dynamic(
  () => import('~/components/organisms/three/scenes/aalto/AaltoCanvas'),
  { ssr: false }
);

type Props = z.infer<typeof schemas.aaltoSceneLayout>;

type NavDirection = -1 | 1;

const AaltoSceneSliceClient: React.FC<Props> = ({ backgroundImage, links }) => {
  const linkRef = useRef<HTMLAnchorElement>(null);

  const [isHovered, setIsHovered] = useState<boolean>(false);
  const [navDirection, setNavDirection] = useState<NavDirection>(1);
  const intersectionRatio = 1;

  const {
    realIndex: realCurrentIndex,
    index: effectiveIndex,
    increment,
    decrement,
  } = useCarousel({
    total: links.length - 1,
    autoplayDelay: 8000,
  });

  const currentElement = links[effectiveIndex];

  const isMobile = useHydrationSafeResponsive('mobile');
  const isFullyVisible = true;

  const isIntersectingRatio = true;

  const shouldLoad = useIdleLoad(true);

  const images: MediaItem[] = useMemo(
    () =>
      links.map((link) => {
        if (link.builtinMedia.type === 'image') {
          if (link.builtinMedia.image?.node.mediaDetails.sizes) {
            const size = link.builtinMedia.image.node.mediaDetails.sizes.find(
              (size) => size.name === 'medium_large'
            );

            if (size) {
              return { type: 'image', url: size.sourceUrl };
            }
          }

          return {
            type: 'image',
            url: link.builtinMedia.image?.node.sourceUrl || '',
          };
        } else if (link.builtinMedia.type === 'video') {
          return {
            type: 'video',
            url: link.builtinMedia.video?.node.mediaItemUrl || '',
          };
        }

        return { type: 'image', url: '' };
      }),
    [links]
  );

  const goToElement = (direction: NavDirection) => {
    setNavDirection(direction);

    if (direction === 1) {
      increment();
    } else {
      decrement();
    }
  };

  const onCurrentElementPointerOver = () => {
    setIsHovered(true);
  };

  const onCurrentElementPointerOut = () => {
    setIsHovered(false);
  };

  const onCurrentElementClicked = (event: ThreeEvent<MouseEvent>) => {
    if (linkRef.current && !isMobile) {
      event.stopPropagation();
      event.nativeEvent.stopImmediatePropagation();

      if (event.ctrlKey || event.metaKey || event.button === 1) {
        const initialTarget = linkRef.current.target;

        linkRef.current.target = '_blank';
        linkRef.current.click();
        linkRef.current.target = initialTarget;
      } else {
        linkRef.current.click();
      }
    }
  };

  return (
    <>
      {backgroundImage && (
        <div className="mask-image-fade-t pointer-events-none absolute top-0 left-0 -z-10 h-full w-full">
          <Media
            media={{
              type: 'image',
              image: { node: backgroundImage.node },
            }}
            imageProps={{
              width: backgroundImage.node.mediaDetails.width,
              height: backgroundImage.node.mediaDetails.height,
              className: 'h-full w-full object-cover pointer-events-none',
            }}
          />
        </div>
      )}

      <div className="pointer-events-none relative z-10 grid h-full w-full grid-cols-2 grid-rows-[1fr_3fr_1fr] flex-col items-center gap-10 px-4 py-10 select-none lg:grid-flow-col lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)] lg:grid-rows-[2fr_1fr_3fr] lg:px-20 lg:py-0">
        <QuatrefoilFloater
          className="max-lg:hidden"
          offsetFromCursor={{ x: 36, y: 36 }}
          followCursor={!isMobile}
          visible={isHovered && !isMobile}
        >
          Voir
        </QuatrefoilFloater>

        <div className="text-brand-quaternary lg:text-brand-tertiary relative col-span-2 flex h-full flex-col justify-center gap-2 lg:col-span-1 lg:row-start-2 lg:h-auto lg:items-start lg:justify-start">
          {currentElement && (
            <AnimatePresence initial={false} mode="popLayout">
              <TextSplitter
                key={
                  isFullyVisible
                    ? `secondary__${currentElement.postObject.node.id}`
                    : '__EMPTY__secondary-title'
                }
                text={
                  isFullyVisible ? currentElement.secondaryTitle || ' ' : ' '
                }
                generateWordProps={({ index }) => ({
                  initial: { y: -10 * navDirection, opacity: 0 },
                  animate: {
                    y: 0,
                    opacity: 1,
                    transition: { duration: 0.25, delay: index * 0.05 },
                  },
                  exit: {
                    y: 10 * navDirection,
                    opacity: 0,
                    transition: { duration: 0.25, delay: index * 0.05 },
                  },
                })}
                useMotion
              />

              <Heading
                key={
                  isFullyVisible
                    ? `primary__${currentElement.postObject.node.id}`
                    : '__EMPTY__primary-title'
                }
              >
                <TextSplitter
                  text={
                    isFullyVisible
                      ? currentElement.primaryTitle ||
                        currentElement.postObject.node.title ||
                        ' '
                      : ' '
                  }
                  generateWordProps={({ index }) => ({
                    initial: { y: -10 * navDirection, opacity: 0 },
                    animate: {
                      y: 0,
                      opacity: 1,
                      transition: { duration: 0.25, delay: index * 0.05 },
                    },
                    exit: {
                      y: 10 * navDirection,
                      opacity: 0,
                      transition: { duration: 0.25, delay: index * 0.05 },
                    },
                  })}
                  useMotion
                />
              </Heading>
            </AnimatePresence>
          )}
        </div>

        <AnimatePresence>
          {isFullyVisible && (
            <>
              <motion.div
                key="controls--previous"
                className="text-brand-primary relative row-start-2 flex justify-start pt-6.5 lg:col-start-2 lg:h-9.5"
                aria-label="Précédent"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <button
                  type="button"
                  className="pointer-events-auto flex aspect-square items-center justify-center p-3 lg:hidden"
                  onClick={() => goToElement(-1)}
                >
                  <TriangleLeft className="h-6 bg-white/70 backdrop-blur-lg" />
                </button>

                <button
                  type="button"
                  className="text-brand-tertiary hocus-visible:text-brand-tertiary/50 pointer-events-auto h-full w-full cursor-pointer py-6 transition-colors duration-500 max-lg:hidden lg:w-auto lg:py-0"
                  onClick={() => goToElement(-1)}
                >
                  Précédent
                </button>
              </motion.div>

              <motion.div
                key="controls--next"
                className="text-brand-primary relative row-start-2 flex justify-end pt-6.5 lg:col-start-3 lg:h-9.5"
                aria-label="Suivant"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <button
                  type="button"
                  className="pointer-events-auto flex aspect-square items-center justify-center p-3 lg:hidden"
                  onClick={() => goToElement(1)}
                >
                  <TriangleRight className="h-6 bg-white/70 backdrop-blur-lg" />
                </button>

                <button
                  type="button"
                  className="text-brand-tertiary hocus-visible:text-brand-tertiary/50 pointer-events-auto h-full w-full cursor-pointer py-6 transition-colors duration-500 max-lg:hidden lg:w-auto lg:py-0"
                  onClick={() => goToElement(1)}
                >
                  Suivant
                </button>
              </motion.div>
            </>
          )}
        </AnimatePresence>

        <div className="relative col-span-2 col-start-1 row-start-3 flex justify-center self-start max-lg:-my-32 lg:hidden">
          <PageLink
            item={currentElement.postObject}
            className="pointer-events-auto relative flex rounded-full"
            aria-label={currentElement.postObject.node.title}
          >
            <QuatrefoilFloater
              className="!max-lg:hidden relative! h-16 w-16"
              classNames={{ innerContainer: 'translate-0' }}
              visible
            >
              Voir
            </QuatrefoilFloater>
          </PageLink>
        </div>
      </div>

      {currentElement && (
        <PageLink
          item={currentElement.postObject}
          className="absolute size-0"
          aria-label={currentElement.postObject.node.title}
          ref={linkRef}
        />
      )}

      <ErrorBoundary
        fallback={
          <GenericFallback className="absolute" variant="error" showIcon>
            Error while rendering WebGL application
          </GenericFallback>
        }
      >
        {shouldLoad && (
          <AaltoCanvas
            images={images}
            realCurrentIndex={realCurrentIndex}
            onCurrentElementPointerOver={onCurrentElementPointerOver}
            onCurrentElementPointerOut={onCurrentElementPointerOut}
            onCurrentElementClicked={onCurrentElementClicked}
            isHovered={isHovered}
            isIntersecting={isIntersectingRatio}
            intersectionRatio={intersectionRatio}
          />
        )}
      </ErrorBoundary>
    </>
  );
};

export default AaltoSceneSliceClient;
